import { useMemo } from 'react';
import { useScenarioLegPhotosQuery } from '../queries/scenarioLegPhotos.query.generated';
import type { OrderCarouselImage } from '@hopdrive/feature-order-index';
import { buildCarouselImages } from '@hopdrive/feature-order-index';

export type ScenarioPhotoPhase = 'pickup' | 'delivery';

export interface ScenarioLegPhotoTarget {
  moveId: number;
  phase: ScenarioPhotoPhase;
}

/**
 * One leg's photos for one phase, fetched only once a viewer asks for them.
 *
 * **Nothing is fetched until a target exists**, and when one does, the query brings back
 * exactly that phase — the other phase's photos, workflow data, and step tree are all gated
 * out by the document. A dealer opening pickup images does not pay for delivery ones.
 *
 * The images come back already run through the portal's carousel pipeline, so the ordering,
 * the labels, the captions, and the **workflow-private step filter** are the portal's.
 */
export const useScenarioLegPhotos = (target: ScenarioLegPhotoTarget | null) => {
  const isPickup = target?.phase === 'pickup';
  const isDelivery = target?.phase === 'delivery';

  const { data, loading } = useScenarioLegPhotosQuery({
    variables: { moveId: target?.moveId ?? 0, isPickup, isDelivery },
    skip: !target,
    fetchPolicy: 'cache-and-network',
  });

  const images = useMemo<OrderCarouselImage[]>(() => {
    const move = data?.moves_by_pk;
    if (!move || !target) return [];

    const workflowData = (move[
      target.phase === 'pickup' ? 'pickup_workflow_data' : 'delivery_workflow_data'
    ] ?? null) as Record<string, unknown> | null;
    const signer = workflowData?.['signer-name'];

    return buildCarouselImages({
      move,
      type: target.phase,
      signer: typeof signer === 'string' ? signer : null,
    });
  }, [data, target]);

  return { images, loading };
};
